import { Avatar, Box, Button, Paper, Typography } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { GangParticipant } from '../../types/gangCreationTypes';

interface GangMemberSelectedListProps {
  participants: GangParticipant[];
  onRemoveUser: (userId: string) => void;
  isLeaderSelection: boolean;
  maxSelections?: number;
  /** IDs of members that exist in the database (from initial gang data) */
  persistedMemberIds?: string[];
  /** When false, hide remove button for persisted members */
  isRemovalEnabled?: boolean;
}

export const GangMemberSelectedList = ({
  participants,
  onRemoveUser,
  isLeaderSelection,
  maxSelections,
  persistedMemberIds = [],
  isRemovalEnabled = true,
}: GangMemberSelectedListProps) => {
  // Determine if a member can be removed:
  // - If feature is enabled, all members can be removed
  // - If feature is disabled, only non-persisted (new) members can be removed
  const canRemoveMember = (userId: string): boolean => {
    if (isRemovalEnabled) return true;
    return !persistedMemberIds.includes(userId);
  };
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Selected {isLeaderSelection ? 'Leaders' : 'Members'}
        {typeof maxSelections === 'number'
          ? ` (${participants.length}/${maxSelections})`
          : ''}
      </Typography>

      {participants.length === 0 ? (
        <EmptySelectedState isLeaderSelection={isLeaderSelection} />
      ) : (
        <Box
          sx={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            backgroundColor: 'background.default',
          }}
        >
          {participants.map(participant => (
            <Paper
              key={participant.userId}
              sx={{ p: 2, mb: 1 }}
              data-testid={`selected-${participant.userId}`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {participant.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body1" fontWeight="medium">
                        {participant.fullName}
                      </Typography>
                      {isLeaderSelection && (
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          Leader
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {participant.email}
                    </Typography>
                  </Box>
                </Box>

                {canRemoveMember(participant.userId) && (
                  <Button
                    data-testid={`remove-selected-${participant.userId}`}
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => onRemoveUser(participant.userId)}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

const EmptySelectedState = ({
  isLeaderSelection,
}: {
  isLeaderSelection: boolean;
}) => (
  <Box
    sx={{
      py: 3,
      textAlign: 'center',
      color: 'text.secondary',
      backgroundColor: 'background.default',
      borderRadius: 1,
      border: '1px dashed',
      borderColor: 'divider',
    }}
  >
    <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
    <Typography variant="body2">
      No {isLeaderSelection ? 'leaders' : 'members'} selected yet. Search and
      click users above to add them.
    </Typography>
  </Box>
);
